import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import CouponApi from "../Api/CouponApi";
import usePermissions from "../hook/usePermissions";
import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import TablePagination from "./TablePagination";

const CouponListLayer = () => {
  const { hasPermission } = usePermissions();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    const handler = setTimeout(() => { setDebouncedSearchTerm(searchTerm); }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    fetchData();
  }, [debouncedSearchTerm, currentPage, rowsPerPage]);

  const fetchData = async () => {
    setLoading(true);
    const response = await CouponApi.getAll(currentPage, rowsPerPage, debouncedSearchTerm);
    if (response.status) {
      setData(response.response.data || []);
      setTotalRecords(response.response.total || 0);
    }
    setLoading(false);
  };

  const handleStatusToggle = async (id) => {
    const response = await CouponApi.statusUpdate(id);
    if (response.status) fetchData();
  };

  const confirmDelete = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (itemToDelete) {
      setLoading(true);
      const response = await CouponApi.delete(itemToDelete._id);
      if (response.status) {
        fetchData();
        setShowDeleteModal(false);
        setItemToDelete(null);
      }
      setLoading(false);
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const totalPages = Math.ceil(totalRecords / rowsPerPage);

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
        <h6 className="text-primary-600 pb-2 mb-0">Coupon Management</h6>
        <div className="d-flex align-items-center gap-3">
          <div className="navbar-search">
            <input type="text" className="bg-base h-40-px w-300-px radius-8" placeholder="Search Coupon Code or Title" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            <Icon icon="ion:search-outline" className="icon" />
          </div>
          {hasPermission("Coupons", "add") && (
            <Link to="/coupons/add" className="btn btn-primary-600 radius-8 px-20 py-11 d-flex align-items-center gap-2 btn-hover-green">
              <Icon icon="ic:baseline-plus" className="text-xl" />
              Add New Coupon
            </Link>
          )}
        </div>
      </div>
      <div className="card-body p-24">
        {/* <div className="d-flex flex-wrap align-items-center justify-content-end gap-3 mb-24">
          <div className="navbar-search">
            <input type="text" className="bg-base h-40-px w-300-px radius-8" placeholder="Search Coupon Code or Title" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            <Icon icon="ion:search-outline" className="icon" />
          </div>
        </div> */}

        <div className="table-responsive">
          <table className="table basic-table mb-0">
            <thead>
              <tr>
                <th style={{ width: "50px" }}>S.No</th>
                <th>Code</th>
                <th>Title</th>
                <th>Discount</th>
                <th>Offer Type</th>
                <th>Validity</th>
                <th>Used / Order Limit</th>
                <th className="text-center">Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="9" className="text-center py-4">Loading...</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan="9" className="text-center py-4">No coupons found.</td></tr>
              ) : (
                data.map((item, index) => (
                  <tr key={item._id}>
                    <td>{currentPage * rowsPerPage + index + 1}</td>
                    <td><span className="fw-bold">{item.code}</span></td>
                    <td>{item.title}</td>
                    <td>
                      {item.discountType === "percentage" ? `${item.discountValue}%` : `₹${item.discountValue}`}
                    </td>
                    <td><span className="badge bg-info-focus text-info-600 px-12 py-6 radius-4 text-xs">{item.offerType.toUpperCase()}</span></td>
                    <td>
                      <div style={{ fontSize: '14px' }}>
                        <div>Start: {new Date(item.startDate).toLocaleDateString()}</div>
                        <div>End: {new Date(item.endDate).toLocaleDateString()}</div>
                      </div>
                    </td>
                    <td>{item.usedCount} / {item.totalLimit}</td>
                    <td className="text-center">
                      <div className="d-flex justify-content-center align-items-center h-100">
                        {hasPermission("Coupons", "edit") && (
                          <button
                            onClick={() => handleStatusToggle(item._id)}
                            className={`status-toggle-btn ${item.status ? 'active' : 'inactive'}`}
                          >
                            <span className="toggle-circle"></span>
                            <span className="toggle-text">{item.status ? "active" : "inactive"}</span>
                          </button>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        {hasPermission("Coupons", "edit") && (
                          <Link to={`/coupons/edit/${item._id}`} className="bg-success-focus text-success-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0">
                            <Icon icon="lucide:edit" className="menu-icon" />
                          </Link>
                        )}
                        {hasPermission("Coupons", "delete") && (
                          <button onClick={() => confirmDelete(item)} className="bg-danger-focus text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0">
                            <Icon icon="fluent:delete-24-regular" className="menu-icon" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && (
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value));
              setCurrentPage(0);
            }}
            totalRecords={totalRecords}
          />
        )}
      </div>

      <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
        <Modal.Body className="text-center p-5">
          <div className="d-flex justify-content-center mb-3">
            <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px">
              <Icon icon="fluent:delete-24-regular" className="text-danger-600 text-xxl" />
            </div>
          </div>
          <h5 className="mb-3">Delete Coupon?</h5>
          <p className="text-secondary-light mb-4">Are you sure you want to delete <span className="fw-bold">{itemToDelete?.code}</span>? This action cannot be undone.</p>
          <div className="d-flex justify-content-center gap-3">
            <Button variant="outline-secondary" className="px-32" onClick={handleCloseDeleteModal}>Cancel</Button>
            <Button variant="danger" className="px-32" onClick={handleDelete} disabled={loading}>{loading ? "Deleting..." : "Yes, Delete It"}</Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default CouponListLayer;
